import { useRef } from "react";
import type { TextCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: TextCanvasObject;
}

// テキストオブジェクト（直接書き込み可能）。
// - 単一クリックで選択、ダブルクリックで編集モードへ
// - 編集中は IME composition を考慮しつつ 50ms debounce で text.update op を送信
export function TextObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
  });

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialRotation: object.rotation,
    disabled: editor.isEditing || activeTool === "draw-pen",
  });

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="text"
      onDoubleClick={editor.enterEditMode}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        minHeight: `${object.h}px`,
        zIndex: object.zOrder,
        fontSize: `${object.fontSize ?? 16}px`,
        color: object.color ?? "var(--color-text)",
        whiteSpace: "pre-wrap",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        // 他オブジェクトと回転支点を揃えるため `object.w / object.h` の中心を pixel
        // 指定する。`minHeight` を使っているため複数行テキストで実描画が h を超えても、
        // CSS の transform-origin は論理中心（useRotationHandle が前提とする
        // `object.x + w/2, object.y + h/2`）と一致する。
        transformOrigin: `${object.w / 2}px ${object.h / 2}px`,
        outline: isSelected ? "2px solid var(--color-accent)" : "none",
        cursor: editor.isEditing ? "text" : "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: editor.isEditing ? "text" : "none",
      }}
    >
      <div
        ref={editorRef}
        data-testid={`canvas-object-text-${object.id}`}
        style={{
          width: "100%",
          minHeight: "100%",
          outline: "none",
          cursor: "inherit",
        }}
        {...editor.editorProps}
      >
        {editor.isEditing ? null : object.text}
      </div>
    </div>
  );
}
